/**
 * Effective operational eligibility for CrewPilot 1.0.
 *
 * Project Intrinsic State (Active/Archived) PLUS Parent Client State.
 * A project is operationally selectable/writable only when it is intrinsically
 * active AND its parent client is not archived.
 *
 * ARCHIVED CLIENT = hard operational lock (no warn-and-allow).
 * History remains readable; settlement of pre-archive invoices remains allowed.
 */

import { isClientArchived } from "./clientVisibility";
import { isProjectArchived } from "./projectVisibility";

export const OPERATIONAL_LOCK_USER_MESSAGE =
  "This client is archived. Restore the client (or choose a different project) before creating new operational work.";

export const OPERATIONAL_LOCK_CLIENT_MESSAGE =
  "This client is archived. Restore the client before creating new estimates, invoices, or projects under it.";

export const CP_CLIENT_ARCHIVED_EVENT = "cp-client-archived";
export const CP_CLIENT_RESTORED_EVENT = "cp-client-restored";

/**
 * True when the client (or archived_at / status hint) is operationally locked.
 * Accepts a client row, `{ archived_at }`, or a raw archived_at string/null.
 */
export function isClientOperationallyLocked(clientOrHint) {
  if (clientOrHint == null) {
    return false;
  }
  if (typeof clientOrHint === "string") {
    return Boolean(clientOrHint);
  }
  if (typeof clientOrHint === "object") {
    if ("archived_at" in clientOrHint || "client_status" in clientOrHint) {
      return isClientArchived(clientOrHint);
    }
  }
  return false;
}

/**
 * Resolve parent-client lock from a project row + optional client map/hint.
 * Prefers embedded `project.clients`, then `clientById[client_id]`, then
 * `clientOrArchivedAt` when it is a direct hint for this project's parent.
 */
export function resolveProjectParentLockHint(
  project,
  clientOrArchivedAt = null,
  clientById = null,
) {
  if (!project) {
    return null;
  }

  if (project.clients && typeof project.clients === "object") {
    return project.clients;
  }

  const clientId = project.client_id;
  if (clientId && clientById && typeof clientById === "object") {
    const mapped = clientById[clientId] ?? clientById.get?.(clientId);
    if (mapped != null) {
      return mapped;
    }
  }

  if (clientOrArchivedAt != null) {
    return clientOrArchivedAt;
  }

  if (
    Object.prototype.hasOwnProperty.call(project, "client_archived_at") ||
    Object.prototype.hasOwnProperty.call(project, "parent_client_archived_at")
  ) {
    return {
      archived_at:
        project.client_archived_at ?? project.parent_client_archived_at ?? null,
    };
  }

  return null;
}

/**
 * Effective operational eligibility.
 * Intrinsically archived projects stay locked even after client restore.
 */
export function isProjectOperationallyActive(
  project,
  clientOrArchivedAt = null,
  clientById = null,
) {
  if (!project) {
    return false;
  }
  if (isProjectArchived(project)) {
    return false;
  }
  const parentHint = resolveProjectParentLockHint(
    project,
    clientOrArchivedAt,
    clientById,
  );
  if (parentHint == null) {
    // Unknown parent state: keep selectable. Authoritative enforcement is at
    // write asserts (with client lookup) and DB triggers (parked migration).
    return true;
  }
  return !isClientOperationallyLocked(parentHint);
}

/**
 * Filter to projects that are operationally selectable for ops pickers.
 * When clientById is omitted, projects with embedded clients.archived_at are
 * filtered; projects without parent hint are kept (caller should load map).
 */
export function filterOperationallyActiveProjects(
  projects = [],
  clientById = null,
) {
  return (projects || []).filter((project) => {
    if (isProjectArchived(project)) {
      return false;
    }
    const hint = resolveProjectParentLockHint(project, null, clientById);
    if (hint == null) {
      return true;
    }
    return !isClientOperationallyLocked(hint);
  });
}

export function assertProjectOperationallyWritable(
  project,
  clientOrArchivedAt = null,
  clientById = null,
) {
  if (
    !isProjectOperationallyActive(project, clientOrArchivedAt, clientById)
  ) {
    const err = new Error(OPERATIONAL_LOCK_USER_MESSAGE);
    err.code = "CP_OPERATIONAL_LOCK";
    throw err;
  }
  return true;
}

export function assertClientOperationallyWritable(clientOrHint) {
  if (isClientOperationallyLocked(clientOrHint)) {
    const err = new Error(OPERATIONAL_LOCK_CLIENT_MESSAGE);
    err.code = "CP_OPERATIONAL_LOCK";
    throw err;
  }
  return true;
}

/**
 * Drop projects belonging to an archived client from a list and clear matching
 * selected ids. Used by time screens on cp-client-archived.
 */
export function dropProjectsForArchivedClient(
  projects = [],
  clientId,
  selectedIds = {},
) {
  const nextProjects = (projects || []).filter(
    (project) => project?.client_id !== clientId,
  );
  const nextSelected = {};
  Object.entries(selectedIds || {}).forEach(([key, value]) => {
    if (!value) {
      nextSelected[key] = value;
      return;
    }
    const stillPresent = nextProjects.some((p) => p.project_id === value);
    nextSelected[key] = stillPresent ? value : "";
  });
  return { projects: nextProjects, selectedIds: nextSelected };
}

export function clearLastProjectIdIfLocked(projects, lastProjectId) {
  if (!lastProjectId) {
    return null;
  }
  const match = (projects || []).find((p) => p.project_id === lastProjectId);
  return match ? lastProjectId : null;
}

export function notifyClientArchived(clientId) {
  if (typeof window === "undefined" || !clientId) {
    return;
  }
  try {
    const lastProjectId = localStorage.getItem("lastProjectId");
    if (lastProjectId) {
      // Screens also clear via event; drop stale lastProjectId when we can.
      // Full validation requires project list — screens handle that.
    }
  } catch {
    // ignore storage errors
  }
  window.dispatchEvent(
    new CustomEvent(CP_CLIENT_ARCHIVED_EVENT, {
      detail: { clientId },
    }),
  );
}

export function notifyClientRestored(clientId) {
  if (typeof window === "undefined" || !clientId) {
    return;
  }
  window.dispatchEvent(
    new CustomEvent(CP_CLIENT_RESTORED_EVENT, {
      detail: { clientId },
    }),
  );
}

/**
 * Build a clientId → client row map for operational filtering.
 */
export function buildClientByIdMap(clients = []) {
  const map = {};
  (clients || []).forEach((client) => {
    if (client?.client_id) {
      map[client.client_id] = client;
    }
  });
  return map;
}
